import {
  MobilePreview,
  ProfileSection,
  TabsSection,
} from '@design-system/components/mobile-preview';
import { extrasVM } from '../Extras.page';
import { toImgSrc } from '@client/new/common/media.helpers';
import {
  DndList,
  DnDParentRenderer,
  getParentDnDProps,
} from '@design-system/components/dndList/DndList';
import { PropsWithChildren } from 'react';
import { _noop } from '@client/new/helpers/general';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { toLegacyGuideURL } from '../viewModel/extras.mappers';
import { Typography } from '@design-system/components/typography/typography';
import * as styles from './ExtrasPreview.css';
import { getVideoThumbnail } from '../video/video.mappers';
import {
  DndParent,
  DnDItem,
} from '@design-system/components/dndList/DndList.utils';

export const ExtrasPreview = () => {
  const { primaryColor, profile, tabs, dispatch, contentIds } =
    extrasVM.useValue();

  if (tabs.length === 0) return null;

  const activeTabId = tabs[0].id;
  return (
    <MobilePreview
      primaryColor={primaryColor}
      bannerImage={toImgSrc(profile.mediaBanner)}
    >
      <ProfileSection profile={profile} primaryColor={primaryColor} />
      <TabsSection
        tabs={tabs}
        activeTabId={activeTabId}
        primaryColor={primaryColor}
      />
      <div className={styles.wrapper}>
        <DndList
          items={
            ((contentIds.items || [])[0]?.items ?? []) as Array<
              DndParent<DnDItem>
            >
          } // TODO: tabs implementation
          onChange={(d) => dispatch('Reorder', d)}
          renderParent={ExtrasPreviewRenderer}
          renderChild={_noop}
        />
      </div>
    </MobilePreview>
  );
};

const ExtrasPreviewRenderer: DnDParentRenderer<
  PropsWithChildren<{ id: string }>
> = ({ provided, item: { id } }) => {
  const vm = extrasVM.useValue();
  const content = vm.contentMap[id];

  if (!content) return null;

  const dndProps = getParentDnDProps(provided);

  if (
    !content.value?.purchaseInfo ||
    content.value.purchaseInfo.type !== 'purchasable' ||
    content.value.purchaseInfo.publicationStatus !== 'published'
  )
    return null;

  switch (content.type) {
    case 'ContentLegacyGuide':
      return (
        <ExtrasCard
          dndProps={dndProps}
          image={toLegacyGuideURL(content.value.contentId)}
          title={content.value.title}
        />
      );
    case 'ContentLink':
      return (
        <ExtrasCard
          dndProps={dndProps}
          image={content.value.coverImage}
          title={content.value.title}
        />
      );
    case 'ContentPDF':
      return (
        <ExtrasCard
          dndProps={dndProps}
          image={content.value.coverURL}
          title={content.value.title}
        />
      );

    case 'ContentSlides':
      return (
        <ExtrasCard
          dndProps={dndProps}
          image={content.value.coverURL}
          title={content.value.title}
        />
      );
    case 'ContentVideo':
      return (
        <ExtrasCard
          dndProps={dndProps}
          image={getVideoThumbnail(content.value)}
          title={content.value.title}
          combined={!content.value.coverURL}
        />
      );
    default:
      return null;
  }
};

const ExtrasCard = (p: {
  combined?: boolean;
  image: string | undefined;
  title: string;
  dndProps: DraggableProvidedDragHandleProps | undefined | null;
}) => {
  if (p.combined && p.image) {
    return (
      <div {...p.dndProps} className={styles.combinedCard}>
        <img src={p.image} alt="Extras" className={styles.image} />
        <div className={styles.textContainer}>
          <Typography variant="title">{p.title}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div
      {...p.dndProps}
      className={p.image ? styles.cardWithImage : styles.card}
    >
      {p.image ? (
        <img src={p.image} alt="Extras" className={styles.image} />
      ) : (
        <Typography className="text-center" variant="title">
          {p.title}
        </Typography>
      )}
    </div>
  );
};
